/**
 * date-fns is a powerful library, but it's incredibly easy to mess up. We need to perform
 * _all_ calculations and formatting in UTC, but the default behaviour of date-fns means
 * that calculations instead happen in the local timezone. This is a recipe for disaster, as DST
 * shifts mean that the local time isn't always a consistent offset vs. UTC.
 *
 * The tl;dr of it all is - do not use date-fns directly! Only ever use it via functions
 * exposed here, and ensure they are set up to use the UTC timezone by default.
 * https://blog.date-fns.org/v40-with-time-zone-support/
 *
 * Doing it this way is preferable to requiring the use of UTCDate everywhere, or relying on
 * every callsite to specify `in: UTC`. It's incredibly easy to overlook this behaviour,
 * as - as of the time of writing - our FE developers are exclusively in zero/positive UTC timezones,
 * meaning we often don't run into issues ourselves.
 *
 * When adding a function to this passthrough, please check the docs (e.g. https://date-fns.org/v4.1.0/docs/add)
 * and if there is an options argument with an 'in' property (e.g. https://date-fns.org/v4.1.0/docs/add#types/AddOptions/9)
 * ensure that this is correctly set to tz('UTC') by default.
 */

import * as DateFns from 'date-fns'
import { tz } from '@date-fns/tz'
import type { Duration } from 'date-fns'

export type { Duration }

/**
 * These functions are nice and simple, with no timezone issues to worry about. Since they
 * can't be configured with an 'in' property, they're safe to export directly.
 */
export const isBefore = DateFns.isBefore
export const isAfter = DateFns.isAfter
export const isEqual = DateFns.isEqual
export const isValid = DateFns.isValid
export const formatDuration = DateFns.formatDuration

/**
 * These functions are influenced by the timezone, so we must set a default 'in' value in
 * the options.
 *
 * Note that some of these functions have @ts-expect-errors related to 'in'. This looks to be
 * a bug, as the documentation indicates it should always be possible to use `tz` with in
 * as it's the primary use case of the property. In addition, the code itself does work as
 * expected, as seen in this sandbox: https://codesandbox.io/p/sandbox/mknmv5.
 *
 * The tl;dr is I expect there's been some overlooked types, since any function with ResultDate
 * in the context complains about TZDate, but anything with Date does not.
 *
 * I've created an issue on the @date-fns/tz repo anyhoo: https://github.com/date-fns/tz/issues/29
 */
export const getDate: (typeof DateFns)['getDate'] = (date, options = {}) =>
  DateFns.getDate(date, {
    ...options,
    in: options.in ?? tz('UTC')
  })

export const getMonth: (typeof DateFns)['getMonth'] = (date, options = {}) =>
  DateFns.getMonth(date, {
    ...options,
    in: options.in ?? tz('UTC')
  })

export const getYear: (typeof DateFns)['getYear'] = (date, options = {}) =>
  DateFns.getYear(date, {
    ...options,
    in: options.in ?? tz('UTC')
  })

export const endOfDay: (typeof DateFns)['endOfDay'] = (date, options = {}) =>
  DateFns.endOfDay(date, {
    ...options,
    //@ts-expect-error - see note above
    in: options.in ?? tz('UTC')
  })

export const lastDayOfMonth: (typeof DateFns)['lastDayOfMonth'] = (
  date,
  options = {}
) =>
  DateFns.lastDayOfMonth(date, {
    ...options,
    //@ts-expect-error - see note above
    in: options.in ?? tz('UTC')
  })

export const startOfMonth: (typeof DateFns)['startOfMonth'] = (
  date,
  options = {}
) =>
  DateFns.startOfMonth(date, {
    ...options,
    //@ts-expect-error - see note above
    in: options.in ?? tz('UTC')
  })

export const startOfDay: (typeof DateFns)['startOfDay'] = (
  date,
  options = {}
) =>
  DateFns.startOfDay(date, {
    ...options,
    //@ts-expect-error - see note above
    in: options.in ?? tz('UTC')
  })

export const parse: (typeof DateFns)['parse'] = (
  dateStr,
  formatStr,
  referenceDate,
  options = {}
) =>
  DateFns.parse(dateStr, formatStr, referenceDate, {
    ...options,
    //@ts-expect-error - see note above
    in: options.in ?? tz('UTC')
  })

export const parseISO: (typeof DateFns)['parseISO'] = (
  argument,
  options = {}
) =>
  DateFns.parseISO(argument, {
    ...options,
    //@ts-expect-error - see note above
    in: options.in ?? tz('UTC')
  })

export const add: (typeof DateFns)['add'] = (date, duration, options = {}) =>
  DateFns.add(date, duration, {
    ...options,
    //@ts-expect-error - see note above
    in: options.in ?? tz('UTC')
  })

export const sub: (typeof DateFns)['sub'] = (date, duration, options = {}) =>
  DateFns.sub(date, duration, {
    ...options,
    //@ts-expect-error - see note above
    in: options.in ?? tz('UTC')
  })

export const setDate: (typeof DateFns)['setDate'] = (
  date,
  dayOfMonth,
  options = {}
) =>
  DateFns.setDate(date, dayOfMonth, {
    ...options,
    //@ts-expect-error - see note above
    in: options.in ?? tz('UTC')
  })

export const differenceInMinutes: (typeof DateFns)['differenceInMinutes'] = (
  laterDate,
  earlierDate,
  options = {}
) =>
  DateFns.differenceInMinutes(laterDate, earlierDate, {
    ...options,
    //@ts-expect-error - see note above
    in: options.in ?? tz('UTC')
  })

export const differenceInDays: (typeof DateFns)['differenceInDays'] = (
  laterDate,
  earlierDate,
  options = {}
) =>
  DateFns.differenceInDays(laterDate, earlierDate, {
    ...options,
    in: options.in ?? tz('UTC')
  })

export const format: (typeof DateFns)['format'] = (
  date,
  formatStr,
  options = {}
) =>
  DateFns.format(date, formatStr, {
    ...options,
    in: options.in ?? tz('UTC')
  })

export const formatISO: (typeof DateFns)['formatISO'] = (date, options = {}) =>
  DateFns.formatISO(date, {
    ...options,
    in: options.in ?? tz('UTC')
  })

export const formatDistanceToNow: (typeof DateFns)['formatDistanceToNow'] = (
  date,
  options = {}
) =>
  DateFns.formatDistanceToNow(date, {
    ...options,
    in: options.in ?? tz('UTC')
  })

export const intervalToDuration: (typeof DateFns)['intervalToDuration'] = (
  interval,
  options = {}
) =>
  DateFns.intervalToDuration(interval, {
    ...options,
    in: options.in ?? tz('UTC')
  })

export const isFirstDayOfMonth: (typeof DateFns)['isFirstDayOfMonth'] = (
  date,
  options = {}
) =>
  DateFns.isFirstDayOfMonth(date, {
    ...options,
    in: options.in ?? tz('UTC')
  })

export const isLastDayOfMonth: (typeof DateFns)['isLastDayOfMonth'] = (
  date,
  options = {}
) =>
  DateFns.isLastDayOfMonth(date, {
    ...options,
    in: options.in ?? tz('UTC')
  })

export const isSameDay: (typeof DateFns)['isSameDay'] = (
  laterDate,
  earlierDate,
  options = {}
) =>
  DateFns.isSameDay(laterDate, earlierDate, {
    ...options,
    in: options.in ?? tz('UTC')
  })

export const formatDistanceStrict: (typeof DateFns)['formatDistanceStrict'] = (
  laterDate,
  earlierDate,
  options = {}
) =>
  DateFns.formatDistanceStrict(laterDate, earlierDate, {
    ...options,
    in: options.in ?? tz('UTC')
  })

export const endOfMonth: (typeof DateFns)['endOfMonth'] = (
  date,
  options = {}
) =>
  DateFns.endOfMonth(date, {
    ...options,
    //@ts-expect-error - see note above
    in: options.in ?? tz('UTC')
  })

export const set: (typeof DateFns)['set'] = (date, values, options = {}) =>
  DateFns.set(date, values, {
    ...options,
    //@ts-expect-error - see note above
    in: options.in ?? tz('UTC')
  })

export const eachDayOfInterval: (typeof DateFns)['eachDayOfInterval'] = (
  interval,
  //@ts-expect-error - see note above
  options = {}
) =>
  DateFns.eachDayOfInterval(interval, {
    ...options,
    in: options.in ?? tz('UTC')
  })

export const eachMonthOfInterval: (typeof DateFns)['eachMonthOfInterval'] = (
  interval,
  //@ts-expect-error - see note above
  options = {}
) =>
  DateFns.eachMonthOfInterval(interval, {
    ...options,
    in: options.in ?? tz('UTC')
  })

export const eachWeekOfInterval: (typeof DateFns)['eachWeekOfInterval'] = (
  interval,
  //@ts-expect-error - see note above
  options = {}
) =>
  DateFns.eachWeekOfInterval(interval, {
    ...options,
    in: options.in ?? tz('UTC')
  })

export { tz }
